<template>
  <component
    :is="element"
    class="product-prices"
    :class="{ [$class('loading')]: loading }"
  >
    <span
      v-if="isSurplus"
      class="text-body-m-regular product-prices__surplus-text"
      v-text="$string('product.prices.surplus_callout')"
    />

    <span
      v-if="formattedCompareAtPrice"
      class="product-prices__discount text-other-sale-price"
      v-text="discount"
    />

    <span
      v-if="formattedCompareAtPrice"
      class="visually-hidden"
      v-text="$string('product.prices.original_price')"
    />

    <span
      v-if="formattedCompareAtPrice"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
      v-text="formattedCompareAtPrice"
    />

    <span
      v-if="useGiftCardRange"
      class="product-prices__price"
      :class="classPrice"
      v-text="giftCardRangeString"
    />

    <span
      v-if="formattedCompareAtPrice"
      class="visually-hidden"
      v-text="$string('product.prices.current_price')"
    />

    <span
      v-if="!useGiftCardRange"
      class="product-prices__price"
      :class="[
        classPrice,
        {
          'product-prices__price--on-sale': formattedCompareAtPrice,
          'product-prices__price--surplus': isSurplus
        },
      ]"
      v-text="formattedPrice"
    />

    <span
      v-if="renderFinalSaleCopy"
      class="product-prices__final-sale"
      v-text="$string('product.prices.final_sale')"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {String} [classCompareAtPrice] - Comare at price text classes.
 * @param {String} [classPrice] - Price text classes.
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [isSurplus] - If product is surplus and should display
 * different styling.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean|Object} giftCardPriceRange - Range of prices, or false.
 * @param {Boolean} renderFinalSaleCopy - Whether to display final sale copy.
 *
 */
import { formatCurrencyCode } from '~/helpers/format'

export default {
  name: 'ProductPrices',

  props: {
    classCompareAtPrice: {
      type: String,
      default: 'text-other-sale-price',
    },
    classPrice: {
      type: String,
      default: 'text-other-sale-price',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    isSurplus: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    giftCardPriceRange: {
      type: [Boolean, Object],
      default: false,
    },
    renderFinalSaleCopy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      moneyFormat: `${formatCurrencyCode()} ${this.$variable('store.preferredMoneyFormat')}`,
    }
  },

  computed: {

    /**
     * Compute discount.
     * @returns {String}
     */
    discount() {
      const percentDifference = this.price / this.compareAtPrice * 100
      const discount = 100 - percentDifference

      return `-${discount.toFixed(0)}%`
    },

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice, this.moneyFormat)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      return this.price ? this.$formatMoney(this.price, this.moneyFormat) : ''
    },

    /**
     * Compute range string for gift cards before variant selection.
     * @returns {String}
     */
    giftCardRangeString() {
      if (this.giftCardPriceRange === false) {
        return this.$string('accessibility.loading')
      }

      const rangeMin = this.$formatMoney(this.giftCardPriceRange.min, this.moneyFormat)
      const rangeMax = this.$formatMoney(this.giftCardPriceRange.max, this.moneyFormat)

      return this.giftCardPriceRange ? `${rangeMin} - ${rangeMax}` : ''
    },

    /**
     * Compute whether to use gift card range.
     * @returns {Boolean}
     */
    useGiftCardRange() {
      return !this.price && this.giftCardPriceRange
    },
  },
}
</script>
